"use client";

/**
 * DetailsPage context
 * Provides shared state and configuration to all details page components
 */

import { createContext, useContext, useMemo } from "react";
import type {
  DetailsPageContextValue,
  DetailsPageEntity,
  DetailsPageConfig,
  DetailsPageState,
  DetailsPageActions,
  DetailsPagePermissions,
  InlineEditState,
  InlineEditActions,
} from "./types";

// ============================================================================
// Context
// ============================================================================

const DetailsPageContext = createContext<DetailsPageContextValue<
  DetailsPageEntity,
  string
> | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

export interface DetailsPageProviderProps<
  TEntity extends DetailsPageEntity = DetailsPageEntity,
  TTabId extends string = string,
> {
  /** Page configuration */
  config: DetailsPageConfig<TTabId>;
  /** Entity ID */
  entityId: string;
  /** Entity data */
  entity: TEntity | null;
  /** Current state */
  state: DetailsPageState<TTabId>;
  /** State actions */
  actions: DetailsPageActions<TTabId>;
  /** Permission helpers */
  permissions: DetailsPagePermissions;
  /** Inline edit state and actions */
  inlineEdit: InlineEditState & InlineEditActions;
  /** Children */
  children: React.ReactNode;
}

// ============================================================================
// Provider
// ============================================================================

export function DetailsPageProvider<
  TEntity extends DetailsPageEntity = DetailsPageEntity,
  TTabId extends string = string,
>({
  config,
  entityId,
  entity,
  state,
  actions,
  permissions,
  inlineEdit,
  children,
}: DetailsPageProviderProps<TEntity, TTabId>) {
  // Memoize the context value to prevent unnecessary re-renders of consumers
  const value = useMemo<DetailsPageContextValue<TEntity, TTabId>>(
    () => ({
      config,
      entityId,
      entity,
      state,
      actions,
      permissions,
      inlineEdit,
    }),
    [config, entityId, entity, state, actions, permissions, inlineEdit],
  );

  return (
    <DetailsPageContext.Provider
      value={
        value as unknown as DetailsPageContextValue<DetailsPageEntity, string>
      }
    >
      {children}
    </DetailsPageContext.Provider>
  );
}

// ============================================================================
// Main Hook
// ============================================================================

export function useDetailsPage<
  TEntity extends DetailsPageEntity = DetailsPageEntity,
  TTabId extends string = string,
>(): DetailsPageContextValue<TEntity, TTabId> {
  const context = useContext(DetailsPageContext);

  if (!context) {
    throw new Error("useDetailsPage must be used within a DetailsPageProvider");
  }

  return context as unknown as DetailsPageContextValue<TEntity, TTabId>;
}

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Get the details page configuration
 */
export function useDetailsPageConfig<
  TTabId extends string = string,
>(): DetailsPageConfig<TTabId> {
  const { config } = useDetailsPage();
  return config as DetailsPageConfig<TTabId>;
}

/**
 * Get the entity data
 */
export function useDetailsPageEntity<
  TEntity extends DetailsPageEntity = DetailsPageEntity,
>(): TEntity | null {
  const { entity } = useDetailsPage<TEntity>();
  return entity;
}

/**
 * Get the entity ID
 */
export function useDetailsPageEntityId(): string {
  const { entityId } = useDetailsPage();
  return entityId;
}

/**
 * Get the details page state
 */
export function useDetailsPageContextState<
  TTabId extends string = string,
>(): DetailsPageState<TTabId> {
  const { state } = useDetailsPage();
  return state as DetailsPageState<TTabId>;
}

/**
 * Get the details page actions
 */
export function useDetailsPageActions<
  TTabId extends string = string,
>(): DetailsPageActions<TTabId> {
  const { actions } = useDetailsPage();
  return actions as DetailsPageActions<TTabId>;
}

/**
 * Get the details page permissions
 */
export function useDetailsPagePermissions(): DetailsPagePermissions {
  const { permissions } = useDetailsPage();
  return permissions;
}

/**
 * Get the inline edit state and actions
 */
export function useDetailsPageInlineEdit(): InlineEditState &
  InlineEditActions {
  const { inlineEdit } = useDetailsPage();
  return inlineEdit;
}

/**
 * Get the active tab
 */
export function useDetailsPageActiveTab<
  TTabId extends string = string,
>(): TTabId {
  const { state } = useDetailsPage<DetailsPageEntity, TTabId>();
  return state.activeTab;
}
